import { Fragment, useState } from "react";
import clsx from "clsx";
import { ArrowPathIcon, ChevronDownIcon, ChevronRightIcon, ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";

import Modal from "../../../layouts/Modal";
import LoadingWheel from "../../shared/LoadingWheel";
import useWilmaCustomers from "../../../data/useWilmaCustomers";
import useQuotePriceSimulation, { IQuotePriceSimulation, ISimulatedCustomerPrice } from "../../../data/useQuotePriceSimulation";
import { QuoteType } from "../../../models/Quote";
import BreakdownView from "./BreakdownView";
import RawJsonViewer from "./RawJsonViewer";
import { QuoteInputs, SegmentedToggle } from "./QuoteBreakdownModal";
import { BreakdownSide, formatPrice, quoteTypeLabel } from "./calculationBreakdown";

const sameId = (a?: string | null, b?: string | null) => !!a && !!b && a.toLowerCase() === b.toLowerCase();

// The quote stores prices truncated to whole pounds, so compare on that.
const samePrice = (stored: number, simulated: number) => Math.trunc(stored) === Math.trunc(simulated);

type NameLookup = (id?: string | null) => { name: string; colour?: string };

const WinnerSummary = ({
  label,
  storedCustomerId,
  storedPrice,
  storedType,
  simulatedCustomerId,
  simulated,
  lookup,
}: {
  label: string;
  storedCustomerId: string | null;
  storedPrice: number;
  storedType: QuoteType;
  simulatedCustomerId: string | null;
  simulated?: { price: number; type: QuoteType };
  lookup: NameLookup;
}) => {
  const matches =
    (!storedCustomerId && !simulatedCustomerId) ||
    (sameId(storedCustomerId, simulatedCustomerId) && !!simulated && samePrice(storedPrice, simulated.price));

  return (
    <div className={clsx("rounded-md border p-3 text-sm", matches ? "border-gray-200" : "border-amber-300 bg-amber-50")}>
      <h4 className="mb-2 font-semibold text-gray-900">{label}</h4>
      <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1">
        <dt className="text-gray-500">On the quote</dt>
        <dd className="text-gray-900">
          {storedCustomerId ? (
            <>
              <span className="font-medium">{lookup(storedCustomerId).name}</span> · {formatPrice(storedPrice)} · {quoteTypeLabel(storedType)}
            </>
          ) : (
            "No price"
          )}
        </dd>
        <dt className="text-gray-500">If priced now</dt>
        <dd className="text-gray-900">
          {simulatedCustomerId && simulated ? (
            <>
              <span className="font-medium">{lookup(simulatedCustomerId).name}</span> · {formatPrice(simulated.price)} · {quoteTypeLabel(simulated.type)}
            </>
          ) : (
            "No price"
          )}
        </dd>
      </dl>
      {!matches && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-800">
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
          Different from the quote, so settings have probably changed since it was priced.
        </p>
      )}
    </div>
  );
};

const CustomerDetail = ({ customer }: { customer: ISimulatedCustomerPrice }) => {
  const [side, setSide] = useState<BreakdownSide>("collection");
  const [view, setView] = useState<"readable" | "raw">("readable");

  return (
    <div className="space-y-3 bg-gray-50 p-3 sm:p-4">
      {!customer.priced && customer.noPriceReason && (
        <p className="text-sm text-gray-700">
          <span className="font-medium">No price: </span>
          {customer.noPriceReason}. The steps it got through before stopping are below.
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedToggle
          value={side}
          onChange={setSide}
          options={[
            { value: "collection", label: `Collection · ${formatPrice(customer.priceCollected)}` },
            { value: "dropOff", label: `Drop Off · ${formatPrice(customer.priceDropOff)}` },
          ]}
        />
        <SegmentedToggle
          value={view}
          onChange={setView}
          options={[
            { value: "readable", label: "Breakdown" },
            { value: "raw", label: "Raw JSON" },
          ]}
        />
      </div>
      {view === "readable" ? (
        <BreakdownView
          calculationJson={customer.calculationJson}
          side={side}
          winningType={side === "collection" ? customer.collectedType : customer.dropOffType}
          total={side === "collection" ? customer.priceCollected : customer.priceDropOff}
        />
      ) : (
        <RawJsonViewer json={customer.calculationJson} maxHeight="max-h-96" />
      )}
    </div>
  );
};

const CustomerTable = ({
  title,
  customers,
  data,
  lookup,
}: {
  title: string;
  customers: ISimulatedCustomerPrice[];
  data: IQuotePriceSimulation;
  lookup: NameLookup;
}) => {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (customers.length === 0) return null;

  return (
    <div>
      <h4 className="mb-2 text-sm font-semibold text-gray-900">
        {title} <span className="font-normal text-gray-500">({customers.length})</span>
      </h4>
      <div className="overflow-x-auto rounded-md border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
            <tr>
              <th className="w-8 px-3 py-2" />
              <th className="px-3 py-2">Customer</th>
              <th className="px-3 py-2 text-right">Collection</th>
              <th className="px-3 py-2 text-right">Drop Off</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {customers.map((customer) => {
              const isOpen = expanded === customer.customerId;
              const { name, colour } = lookup(customer.customerId);
              const winsCollection = sameId(customer.customerId, data.simulatedCollectedCustomerId);
              const winsDropOff = sameId(customer.customerId, data.simulatedDropOffCustomerId);

              return (
                <Fragment key={customer.customerId}>
                  <tr
                    className={clsx("cursor-pointer hover:bg-gray-50", !customer.competes && "text-gray-500")}
                    onClick={() => setExpanded(isOpen ? null : customer.customerId)}
                  >
                    <td className="px-3 py-2 text-gray-400">
                      {isOpen ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: colour ?? "#9ca3af" }} />
                        <span className="font-medium">{name}</span>
                        {winsCollection && <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-800">Wins collection</span>}
                        {winsDropOff && <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-medium text-orange-800">Wins drop off</span>}
                      </div>
                      {customer.notCompetingReason && (
                        <div className="mt-0.5 text-xs text-gray-500">
                          <span className="font-medium">Not competing:</span> {customer.notCompetingReason}
                        </div>
                      )}
                      {customer.noPriceReason && (
                        <div className="mt-0.5 text-xs text-red-600">
                          <span className="font-medium">No price:</span> {customer.noPriceReason}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                      <div className={clsx(winsCollection && "font-semibold text-green-700")}>{formatPrice(customer.priceCollected)}</div>
                      {customer.priceCollected >= 0 && <div className="text-xs text-gray-500">{quoteTypeLabel(customer.collectedType)}</div>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                      <div className={clsx(winsDropOff && "font-semibold text-orange-700")}>{formatPrice(customer.priceDropOff)}</div>
                      {customer.priceDropOff >= 0 && <div className="text-xs text-gray-500">{quoteTypeLabel(customer.dropOffType)}</div>}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={4} className="p-0">
                        <CustomerDetail customer={customer} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * SAV only: re-prices the quote against every customer covering its postcode with their current
 * settings, so "why did this get £X?" can be answered without digging through each customer.
 */
export default function SimulatePricesModal({
  quoteId,
  open,
  setOpen,
}: {
  quoteId: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { simulation } = useQuotePriceSimulation(quoteId, open);
  const { customers: customerNames } = useWilmaCustomers();

  const lookup: NameLookup = (id) => {
    const match = customerNames.data?.data.find((c) => sameId(c.id, id));
    return { name: match?.name ?? "Unknown customer", colour: match?.primaryColour };
  };

  const data = simulation.data?.data;
  const find = (id: string | null) => data?.customers.find((c) => sameId(c.customerId, id));
  const collectionWinner = find(data?.simulatedCollectedCustomerId ?? null);
  const dropOffWinner = find(data?.simulatedDropOffCustomerId ?? null);

  return (
    <Modal width="max-w-6xl" open={open} setOpen={setOpen}>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Simulate quote prices</h3>
            <p className="text-sm text-gray-500">
              Prices this quote again for every customer covering the postcode, using their settings as they are right now. Nothing on the quote is changed.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => simulation.refetch()}
              disabled={simulation.isFetching}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowPathIcon className={clsx("h-4 w-4", simulation.isFetching && "animate-spin")} />
              Re-run
            </button>
            <button type="button" onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {simulation.isLoading ? (
          <div className="py-10">
            <LoadingWheel />
          </div>
        ) : simulation.isError || !data ? (
          <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">
            {(simulation.error as Error)?.message || "Couldn't simulate prices for this quote."}
          </p>
        ) : (
          <>
            <QuoteInputs quote={{ vehicleRegistration: data.vehicleRegistration, postcode: data.postcode, ...data.inputs }} />

            <p className="text-xs text-gray-500">
              {data.isMarketplaceQuote
                ? "Marketplace quote: every customer covering this postcode with SellAVehicle quotes turned on competes, and the highest price wins."
                : "Customer website quote: only the website's own customer competes. Other customers are shown for comparison."}
            </p>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <WinnerSummary
                label="Collection"
                storedCustomerId={data.storedCollectedCustomerId}
                storedPrice={data.storedPriceCollected}
                storedType={data.storedCollectedType}
                simulatedCustomerId={data.simulatedCollectedCustomerId}
                simulated={collectionWinner && { price: collectionWinner.priceCollected, type: collectionWinner.collectedType }}
                lookup={lookup}
              />
              <WinnerSummary
                label="Drop Off"
                storedCustomerId={data.storedDropOffCustomerId}
                storedPrice={data.storedPriceDropOff}
                storedType={data.storedDropOffType}
                simulatedCustomerId={data.simulatedDropOffCustomerId}
                simulated={dropOffWinner && { price: dropOffWinner.priceDropOff, type: dropOffWinner.dropOffType }}
                lookup={lookup}
              />
            </div>

            {data.customers.length === 0 ? (
              <p className="text-sm text-gray-500">No customer has any postcode setup for {data.postcode}.</p>
            ) : (
              <>
                <p className="text-xs text-gray-500">Click a customer to see how their price was worked out.</p>
                <CustomerTable title="Competing for this quote" customers={data.customers.filter((c) => c.competes)} data={data} lookup={lookup} />
                <CustomerTable title="Not competing" customers={data.customers.filter((c) => !c.competes)} data={data} lookup={lookup} />
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
